import axios from "axios";
import cookie from "react-cookies";

// Variables to handle multiple simultaneous 401 failures
let isRefreshing = false;
let failedQueue = [];
let logoutHandler = null;

export const setLogoutHandler = (handler) => {
  logoutHandler = handler;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const api = axios.create({
  headers: {
    "Content-Type": "application/json",
    // "ngrok-skip-browser-warning": true,
  },
});

// Request interceptor to add the auth token header to every request
api.interceptors.request.use(
  (config) => {
    const token = cookie.load("token") || cookie.load("jwttoken");
    if (token) {
      config.headers["Authorization"] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle 401 Unauthorized errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is 401 and haven't been retried already
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = cookie.load("token") || cookie.load("jwttoken");

        // Refresh token
        const response = await axios.post(
          "/api/refreshToken",
          {},
          {
            headers: { Authorization: token },
          },
        );

        if (response.data.statusCode === 200) {
          const newToken = response.data.data.token;
          // Set cookie expiry to 15 minutes
          const expiry = new Date(Date.now() + 15 * 60 * 1000);

          cookie.save("token", newToken, { path: "/", expires: expiry });
          cookie.save("jwttoken", newToken, { path: "/", expires: expiry });

          // Retry original request with new token
          api.defaults.headers.common["Authorization"] = newToken;
          originalRequest.headers["Authorization"] = newToken;

          processQueue(null, newToken);
          return api(originalRequest);
        } else {
          throw new Error("Refresh failed");
        }
      } catch (refreshError) {
        processQueue(refreshError, null);

        if (logoutHandler) {
          logoutHandler();
        } else {
          cookie.remove("token", { path: "/" });
          cookie.remove("jwttoken", { path: "/" });
          window.location.href = "/";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

export default api;
